import catalogEn from './exercises/catalog_en.json';
import catalogUk from './exercises/catalog_uk.json';
import curatedExerciseGuides from './curatedExerciseGuides.json';

const exerciseMapEn = new Map(catalogEn.map(e => [e.id, e]));
const exerciseMapUk = new Map(catalogUk.map(e => [e.id, e]));

function findExercise(exerciseId, isUk) {
    if (isUk) {
        return exerciseMapUk.get(exerciseId) || exerciseMapEn.get(exerciseId);
    }
    return exerciseMapEn.get(exerciseId) || exerciseMapUk.get(exerciseId);
}

const MUSCLE_NAMES = {
    en: {
        chest: "Chest",
        back: "Back",
        upper_back: "Upper Back",
        lower_back: "Lower Back",
        trapezius: "Trapezius",
        deltoids: "Shoulders",
        rear_deltoids: "Rear Delts",
        biceps: "Biceps",
        triceps: "Triceps",
        forearms: "Forearms",
        abs: "Abs",
        obliques: "Obliques",
        legs: "Legs",
        quadriceps: "Quadriceps",
        hamstrings: "Hamstrings",
        glutes: "Glutes",
        calves: "Calves",
        hip_flexors: "Hip Flexors"
    },
    uk: {
        chest: "Грудні м'язи",
        back: "М'язи спини",
        upper_back: "Верх спини",
        lower_back: "Поперек",
        trapezius: "Трапеції",
        deltoids: "Дельтоподібні м'язи (плечі)",
        rear_deltoids: "Задні дельти",
        biceps: "Біцепс",
        triceps: "Трицепс",
        forearms: "Передпліччя",
        abs: "Прес",
        obliques: "Косі м'язи живота",
        legs: "М'язи ніг",
        quadriceps: "Квадрицепси",
        hamstrings: "Біцепс стегна (задня поверхня)",
        glutes: "Сідничні м'язи",
        calves: "Литки",
        hip_flexors: "Згиначі стегна"
    }
};

const EQUIPMENT_NAMES = {
    en: {
        Barbell: "Barbell",
        Dumbbell: "Dumbbells",
        Dumbbells: "Dumbbells",
        Cable: "Cable Machine",
        Machine: "Machine",
        Bodyweight: "Bodyweight",
        'Smith Machine': "Smith Machine",
        Kettlebell: "Kettlebell",
        'Cardio Machine': "Cardio Machine",
        'Medicine Ball': "Medicine Ball",
        Functional: "Functional Equipment",
        'Specialty Bar': "Specialty Bar",
        Other: "Standard Gym Equipment"
    },
    uk: {
        Barbell: "Штанга",
        Dumbbell: "Гантелі",
        Dumbbells: "Гантелі",
        Cable: "Блоковий тренажер (кросовер)",
        Machine: "Тренажер",
        Bodyweight: "Власна вага",
        'Smith Machine': "Тренажер Сміта",
        Kettlebell: "Гиря",
        'Cardio Machine': "Кардіотренажер",
        'Medicine Ball': "Медбол",
        Functional: "Функціональне спорядження",
        'Specialty Bar': "Спеціальний гриф",
        Other: "Спортивний інвентар"
    }
};

// Handcrafted, deeply detailed guides for staple gym movements loaded from curatedExerciseGuides.json
export const CURATED_GUIDES = curatedExerciseGuides;

export function isCuratedExercise(exerciseId) {
    return Boolean(exerciseId && CURATED_GUIDES[exerciseId]);
}

/**
 * Algorithmic Guide Generator for any exercise based on muscle, equipment, and movement.
 */
function generateDynamicGuide(exercise, lang) {
    const isUk = lang === 'uk';
    const name = exercise.name || exercise.id;
    const primaryMuscle = exercise.primaryMuscle || 'chest';
    const muscleName = (MUSCLE_NAMES[lang] && MUSCLE_NAMES[lang][primaryMuscle]) || primaryMuscle;
    const equipName = (EQUIPMENT_NAMES[lang] && EQUIPMENT_NAMES[lang][exercise.equipment]) || exercise.equipment || (isUk ? "обладнання" : "equipment");

    const idLower = (exercise.id || "").toLowerCase();
    const nameLower = (name || "").toLowerCase();

    // Categorize pattern
    let pattern = 'general';
    if (idLower.includes('press') || nameLower.includes('press') || idLower.includes('push_up') || idLower.includes('dip')) {
        pattern = 'press';
    } else if (idLower.includes('squat') || nameLower.includes('squat') || idLower.includes('lunge') || idLower.includes('step_up')) {
        pattern = 'squat';
    } else if (idLower.includes('deadlift') || idLower.includes('hinge') || idLower.includes('good_morning') || idLower.includes('thrust')) {
        pattern = 'hinge';
    } else if (idLower.includes('row') || idLower.includes('pull') || idLower.includes('lat') || idLower.includes('chin')) {
        pattern = 'pull';
    } else if (idLower.includes('curl') || nameLower.includes('curl')) {
        pattern = 'curl';
    } else if (idLower.includes('extension') || idLower.includes('pushdown') || idLower.includes('crusher')) {
        pattern = 'extension';
    } else if (idLower.includes('raise') || idLower.includes('fly') || idLower.includes('lateral')) {
        pattern = 'raise';
    } else if (idLower.includes('crunch') || idLower.includes('plank') || idLower.includes('situp') || idLower.includes('twist') || idLower.includes('rollout')) {
        pattern = 'core';
    } else if (idLower.includes('calf') || idLower.includes('calves')) {
        pattern = 'calves';
    }

    if (isUk) {
        let instructions = [];
        let mistakes = [];
        let tips = "";
        let volume = "3–4 підходи по 8–12 повторень із контрольованим темпом.";

        switch (pattern) {
            case 'press':
                instructions = [
                    `Займіть стабільне вихідне положення, використовуючи ${equipName}. Зафіксуйте корпус, зведіть лопатки та підтримуйте природний прогин у спині.`,
                    `Візьміться надійним хватом, розташувавши кисті строго на одній лінії з передпліччями для захисту суглобів.`,
                    `Зробіть глибокий вдих і плавно опустіть вагу під контролем, зберігаючи постійне напруження в ${muscleName}.`,
                    `У нижній точці відчуйте розтягнення цільових м'язів без різких відскоків.`,
                    `Потужним рухом вичавіть вагу у вихідне положення, роблячи видих на зусиллі, не блокуючи суглоби намертво.`
                ];
                mistakes = [
                    "Використання інерції або відскоку замість ізольованої роботи м'язів.",
                    "Неправильне положення ліктів, що створює зайве навантаження на плечові суглоби.",
                    "Втрата напруження в корі та надмірне вигинання спини."
                ];
                tips = `Зосередьтеся на відчутті роботи ${muscleName}. Контролюйте фазу опускання не менше 2 секунд.`;
                volume = "3–4 підходи по 6–10 повторень (відпочинок 90–120 с).";
                break;

            case 'pull':
                instructions = [
                    `Налаштуйте ${equipName} та займіть надійну позицію з прямим хребтом та розправленими плечима.`,
                    `Візьміться за рукоятку або гриф, зробіть передпопереднє натягнення та розправте лопатки.`,
                    `Почніть тягу з відведення ліктів назад, концентруючись на скороченні ${muscleName}.`,
                    `У точці пікового скорочення зведіть лопатки та затримайте рух на 1 секунду.`,
                    `Повільно поверніться у вихідне положення, відчуваючи плавне розтягнення спини.`
                ];
                mistakes = [
                    "Тяга руками замість активації найширших м'язів та зведення лопаток.",
                    "Розгойдування корпусом для підкидання ваги за рахунок інерції.",
                    "Округлення спини у фазі розтягнення."
                ];
                tips = "Тягніть ліктями назад, а не долонями, щоб максимально задіяти м'язи спини.";
                volume = "3–4 підходи по 8–12 повторень.";
                break;

            case 'squat':
                instructions = [
                    `Станьте стійко, утримуючи ${equipName}. Стопи поставте на ширині плечей, носки розведіть під природним кутом.`,
                    `Напружте м'язи пресу, зведіть лопатки та тримайте погляд спрямованим вперед.`,
                    `Почніть рух із згинання колін та відведення таза назад, спрямовуючи коліна за лінією носків.`,
                    `Опустіться до комфортної глибини (стегна паралельні підлозі), утримуючи вагу по центру стопи.`,
                    `Відштовхніться серединою стопи та підніміться у вихідне положення, видихаючи у верхній третині амплітуди.`
                ];
                mistakes = [
                    "Зведення колін всередину під час підйому.",
                    "Відрив п'ят від підлоги через зміщення центру ваги на носки.",
                    "Округлення поперекового відділу спини в нижній точці."
                ];
                tips = "Тримайте груди розправленими, а коліна розводьте за напрямком пальців ніг.";
                volume = "3–4 підходи по 8–12 повторень.";
                break;

            case 'hinge':
                instructions = [
                    `Займіть стійку зі стопами на ширині таза, утримуючи ${equipName}.`,
                    `Зафіксуйте пряме положення хребта, розправте плечі та трохи пом'якшіть коліна.`,
                    `Відводьте таз назад, нахиляючи корпус уперед за рахунок кульшових суглобів, зберігаючи поперек рівним.`,
                    `Опускайте вагу вздовж ніг, поки не відчуєте сильне натягнення в задній поверхні стегна та сідницях.`,
                    `Потужним рухом виштовхніть таз уперед за рахунок скорочення сідниць та поверніться у вертикальне положення.`
                ];
                mistakes = [
                    "Округлення попереку під час нахилу вперед.",
                    "Надмірне згинання колін (перетворення тяги на присідання).",
                    "Відведення ваги далеко від лінії ніг."
                ];
                tips = "Спрямовуйте рух тазом назад, немов намагаєтеся торкнутися стіни позаду себе.";
                volume = "3–4 підходи по 8–10 повторень.";
                break;

            case 'curl':
                instructions = [
                    `Встаньте або сядьте прямо, утримуючи ${equipName} у витягнутих руках вздовж корпусу.`,
                    `Притисніть лікті до боків і тримайте плечі опущеними та нерухомими.`,
                    `На видиху зігніть руки в ліктях, піднімаючи вагу виключно за рахунок скорочення ${muscleName}.`,
                    `У верхній точці зафіксуйте пікове скорочення біцепса на 1 секунду.`,
                    `Плавно та під контролем опустіть вагу за 2–3 секунди у вихідне положення до повного розгинання.`
                ];
                mistakes = [
                    "Розгойдування тулубом (читінг) для закидання ваги вгору.",
                    "Висування ліктів уперед, що знімає навантаження з біцепса.",
                    "Неповне опускання ваги вниз."
                ];
                tips = "Тримайте лікті строго нерухомими біля тулуба протягом усього підходу.";
                volume = "3–4 підходи по 10–12 повторень.";
                break;

            case 'extension':
                instructions = [
                    `Налаштуйте ${equipName} та зафіксуйте стабільне положення тіла з напруженим кором.`,
                    `Розташуйте лікті у фіксованому положенні, звідки відбуватиметься ізольований рух.`,
                    `Розігніть руки або ноги в суглобах, направляючи зусилля безпосередньо в ${muscleName}.`,
                    `У точці повного розгинання відчуйте максимальне скорочення м'яза.`,
                    `Контрольовано поверніться у вихідне положення, утримуючи темп без ривків.`
                ];
                mistakes = [
                    "Зміщення положення суглобів під час виконання руху.",
                    "Різке відпускання ваги у зворотній фазі.",
                    "Використання занадто важкої ваги, що порушує ізоляцію."
                ];
                tips = "Рухайтеся виключно в цільовому суглобі, всі інші частини тіла мають бути зафіксовані.";
                volume = "3–4 підходи по 10–15 повторень.";
                break;

            case 'raise':
                instructions = [
                    `Станьте прямо, тримаючи ${equipName}. Злегка нахиліть корпус уперед, напружте прес.`,
                    `Руки злегка зігнуті в ліктях, плечі опущені донизу.`,
                    `Підніміть руки через боки або вперед до рівня паралелі з підлогою, концентруючись на ${muscleName}.`,
                    `Затримайтеся на частку секунди у верхній точці амплітуди.`,
                    `Плавно опустіть вагу під контролем, не кидаючи її вниз.`
                ];
                mistakes = [
                    "Підкидання ваги ногами або спиною (читінг).",
                    "Підйом плечей до вух за рахунок трапецій.",
                    "Прямі заблоковані лікті під час руху."
                ];
                tips = "Уявіть, що ви виливаєте воду з глечиків у верхній точці, щоб навантажити бічні дельти.";
                volume = "3–4 підходи по 12–15 повторень.";
                break;

            case 'core':
                instructions = [
                    `Займіть вихідне положення на килимку або тренажері, використовуючи ${equipName}.`,
                    `Притисніть поперек або зафіксуйте нейтральну лінію хребта, напружте глибокі м'язи живота.`,
                    `Виконайте рух за рахунок скорочення м'язів пресу, наближаючи грудну клітку до таза.`,
                    `Зробіть повний видих у точці максимального стискання м'язів кору.`,
                    `Повільно поверніться у вихідну позицію, не розслабляючи прес повністю.`
                ];
                mistakes = [
                    "Тяга голови та шиї руками, що створює травматичне навантаження на хребці.",
                    "Прогин у попереку та відрив нижньої частини спини від опори.",
                    "Затримка дихання під час підходу."
                ];
                tips = "Зосередьтеся на скороченні відстані між нижніми ребрами та тазом, а не на амплітуді підйому.";
                volume = "3–4 підходи по 15–20 повторень або утримання 30–45 секунд.";
                break;

            case 'calves':
                instructions = [
                    `Займіть положення на платформі або тренажері, використовуючи ${equipName}. Подушечки стоп щільно притиснуті до краю.`,
                    `Опустіть п'яти нижче лінії платформи, відчувши повне розтягнення литкових м'язів.`,
                    `Потужно підніміться на носки якомога вище за рахунок скорочення ${muscleName}.`,
                    `Зафіксуйте пікове положення на 1–2 секунди у верхній точці.`,
                    `Повільно опустіться у вихідне положення з глибоким розтягненням.`
                ];
                mistakes = [
                    "Пружне відскакування на ахіллових сухожиллях без паузи.",
                    "Неповна амплітуда підйому на носки.",
                    "Згинання в колінах під час виконання вправи стоячи."
                ];
                tips = "Обов'язково робіть паузу в 1 секунду внизу для гасіння еластичної енергії сухожилля.";
                volume = "3–4 підходи по 12–15 повторень із повною амплітудою.";
                break;

            default:
                instructions = [
                    `Підготуйте ${equipName} та займіть стійке анатомічне положення.`,
                    `Напружте м'язи кору, зафіксуйте суглоби та спрямуйте увагу на цільову групу (${muscleName}).`,
                    `Виконайте фазу скорочення плавно за 1–2 секунди, утримуючи повний контроль над рухом.`,
                    `Зробіть видих у фазі максимального навантаження та зафіксуйте позицію на секунду.`,
                    `Опустіть або поверніть вагу у вихідне положення за 2–3 секунди, зберігаючи напруження.`
                ];
                mistakes = [
                    "Використання ривків та неконтрольованої інерції.",
                    "Порушення правильної постави та вигину хребта.",
                    "Неповна робоча амплітуда руху."
                ];
                tips = `Фокусуйтеся на зв'язку мозок-м'яз для якісного залучення ${muscleName}.`;
                volume = "3–4 підходи по 10–12 повторень.";
                break;
        }

        return { instructions, commonMistakes: mistakes, tips, volume };
    } else {
        // English dynamic guide
        let instructions = [];
        let mistakes = [];
        let tips = "";
        let volume = "3–4 sets of 8–12 reps with controlled tempo.";

        switch (pattern) {
            case 'press':
                instructions = [
                    `Set up in a stable position utilizing your ${equipName}. Keep your core braced, shoulder blades retracted, and maintain a natural spinal arch.`,
                    `Grip firmly with wrists stacked directly above your forearms to protect joint integrity.`,
                    `Inhale and lower the weight smoothly under strict control, keeping constant tension on the ${muscleName}.`,
                    `Feel a safe, full stretch across the target musculature without bouncing.`,
                    `Press the weight powerfully back to the starting position while exhaling past the sticking point. Do not harshly lock out joints.`
                ];
                mistakes = [
                    "Using momentum or bouncing instead of muscular tension.",
                    "Flaring elbows excessively wide, stressing the shoulder joints.",
                    "Losing core tightness and overarching the lower back."
                ];
                tips = `Focus on actively squeezing the ${muscleName} at the peak of each repetition.`;
                volume = "3–4 sets of 6–10 reps (90–120s rest).";
                break;

            case 'pull':
                instructions = [
                    `Position yourself firmly on your ${equipName} with your spine neutral and shoulders set down and back.`,
                    `Grip the handle or bar firmly, taking up all slack before initiating movement.`,
                    `Drive your elbows backward, focusing on contracting the ${muscleName}.`,
                    `Squeeze your shoulder blades together at the top of the movement and hold for a one-second pause.`,
                    `Lower the weight under complete control over 2–3 seconds back to the fully stretched starting position.`
                ];
                mistakes = [
                    "Pulling with the biceps rather than initiating with the lats and scapulae.",
                    "Rocking your torso excessively to heave the weight up.",
                    "Rounding your upper back during the eccentric stretch."
                ];
                tips = "Think of pulling through your elbows to maximize back recruitment.";
                volume = "3–4 sets of 8–12 reps.";
                break;

            case 'squat':
                instructions = [
                    `Stand with a stable stance using your ${equipName}, feet shoulder-width apart and toes angled slightly outward.`,
                    `Brace your core tightly, pull your shoulders back, and keep your gaze straight ahead.`,
                    `Hinge at the hips and bend your knees simultaneously, keeping knees tracking in line with your toes.`,
                    `Descend under control until your thighs are at least parallel to the floor, balancing weight across midfoot.`,
                    `Drive through the midfoot and heels back to the top position, exhaling as you complete the ascent.`
                ];
                mistakes = [
                    "Letting knees cave inward during the drive upward.",
                    "Lifting heels off the ground and shifting weight to the toes.",
                    "Rounding the lumbar spine at the bottom of the squat."
                ];
                tips = "Keep your chest high and push the floor away as you rise.";
                volume = "3–4 sets of 8–12 reps.";
                break;

            case 'hinge':
                instructions = [
                    `Stand with feet hip-width apart holding your ${equipName}.`,
                    `Keep your spine rigid, shoulders pulled back, and knees slightly soft.`,
                    `Push your hips back as your torso tilts forward, keeping the weight close to your legs.`,
                    `Descend until you feel a deep stretch in your hamstrings and glutes without rounding your spine.`,
                    `Drive your hips forward by squeezing your glutes to return to a tall standing posture.`
                ];
                mistakes = [
                    "Rounding the lower back during the forward hinge.",
                    "Bending knees excessively, turning the hinge into a squat.",
                    "Letting the weight drift away from the center of gravity."
                ];
                tips = "Imagine pushing your hips back to touch a wall behind you.";
                volume = "3–4 sets of 8–10 reps.";
                break;

            case 'curl':
                instructions = [
                    `Stand or sit tall holding your ${equipName} with arms fully extended.`,
                    `Pin your elbows close to your torso and keep your shoulders depressed and still.`,
                    `Curl the weight upward by flexing your elbows, focusing entirely on contracting the ${muscleName}.`,
                    `Squeeze hard at the peak contraction for a brief pause.`,
                    `Lower the weight under steady control over 2–3 seconds to a full arm extension.`
                ];
                mistakes = [
                    "Swinging your torso to fling the weights up.",
                    "Allowing elbows to drift forward, decreasing bicep tension.",
                    "Cutting the range of motion short at the bottom."
                ];
                tips = "Keep your upper arms locked in place like hinges.";
                volume = "3–4 sets of 10–12 reps.";
                break;

            case 'extension':
                instructions = [
                    `Secure your position on your ${equipName} with your core braced.`,
                    `Anchor your joints in a fixed position to isolate the movement.`,
                    `Extend your limbs smoothly, channeling force directly through the ${muscleName}.`,
                    `Hold the fully extended position for a deliberate contraction.`,
                    `Return to the start position with a slow, controlled negative phase.`
                ];
                mistakes = [
                    "Letting other body parts shift to assist the movement.",
                    "Allowing the weight to slam back down without resistance.",
                    "Using excessive weight that compromises isolation."
                ];
                tips = "Isolate movement purely to the active joint without shoulder or hip sway.";
                volume = "3–4 sets of 10–15 reps.";
                break;

            case 'raise':
                instructions = [
                    `Stand tall holding your ${equipName}. Maintain a slight forward torso lean and engaged core.`,
                    `Keep a soft bend in your elbows with shoulders pushed down away from your ears.`,
                    `Raise the weights outward or upward until parallel to the floor, leading with the ${muscleName}.`,
                    `Hold briefly at the top of the range of motion.`,
                    `Lower under control over 2 seconds without letting weights drop.`
                ];
                mistakes = [
                    "Using body momentum or leg drive to bounce weights up.",
                    "Shrugging your traps up toward your ears.",
                    "Locking elbows completely straight."
                ];
                tips = "Lead with the elbows and pour imaginary pitchers of water at the top.";
                volume = "3–4 sets of 12–15 reps.";
                break;

            case 'core':
                instructions = [
                    `Position yourself on the mat or apparatus with your ${equipName}.`,
                    `Press your lower back flat and brace your deep abdominal wall.`,
                    `Initiate the movement by flexing your spine or hips using pure core contraction.`,
                    `Exhale completely at peak contraction, bringing ribs toward your pelvis.`,
                    `Return slowly to the starting position without releasing abdominal tension.`
                ];
                mistakes = [
                    "Pulling on your neck or head with your hands.",
                    "Arching the lower back away from the floor or pad.",
                    "Holding your breath throughout the set."
                ];
                tips = "Focus on bringing your lower ribs closer to your pelvis.";
                volume = "3–4 sets of 15–20 reps or 30–45s isometric hold.";
                break;

            case 'calves':
                instructions = [
                    `Position the balls of your feet on the edge of the platform with your ${equipName}.`,
                    `Lower your heels below the edge until you feel a deep stretch in the calves.`,
                    `Press through the balls of your feet to raise up as high as possible onto your toes.`,
                    `Hold the peak contraction for a solid 1–2 seconds.`,
                    `Lower down slowly, pausing for a moment at the bottom to eliminate elastic bounce.`
                ];
                mistakes = [
                    "Bouncing rapidly on your Achilles tendon.",
                    "Cutting the top contraction short.",
                    "Bending knees during standing calf raises."
                ];
                tips = "Pause for a full second at the bottom to remove elastic recoil.";
                volume = "3–4 sets of 12–15 reps with full range.";
                break;

            default:
                instructions = [
                    `Set up with your ${equipName} in a balanced, athletic stance.`,
                    `Engage your core, stabilize your posture, and focus attention on the ${muscleName}.`,
                    `Execute the contraction phase smoothly over 1–2 seconds with full control.`,
                    `Exhale during peak effort and hold the position for a moment.`,
                    `Lower or return to the start position over 2–3 seconds, preserving continuous tension.`
                ];
                mistakes = [
                    "Rushing reps and utilizing uncontrolled momentum.",
                    "Losing spinal alignment and core bracing.",
                    "Restricting range of motion."
                ];
                tips = `Establish a strong mind-muscle connection with the ${muscleName}.`;
                volume = "3–4 sets of 10–12 reps.";
                break;
        }

        return { instructions, commonMistakes: mistakes, tips, volume };
    }
}

export const MUSCLE_IMAGE_MAP = {
    trapezius: 'Trapezius',
    deltoids: 'Deltoids',
    rear_deltoids: 'Rear_Deltoids',
    chest: 'Chest',
    upper_back: 'Upper_Back',
    back: 'Back',
    lower_back: 'Lower_Back',
    biceps: 'Biceps',
    triceps: 'Triceps',
    forearms: 'Forearms',
    abs: 'Abs',
    obliques: 'Obliques',
    hip_flexors: 'Hip_Flexor',
    glutes: 'Glutes',
    legs: 'Legs',
    quadriceps: 'Quadriceps',
    hamstrings: 'Hamstrings',
    calves: 'Calves'
};

export const PATTERN_NAMES = {
    en: {
        horizontal_press: "Horizontal Press",
        incline_press: "Incline Press",
        decline_press: "Decline Press",
        overhead_press: "Vertical Press",
        squat: "Squat Pattern",
        hinge: "Hip Hinge",
        lunge: "Unilateral Leg Pattern",
        vertical_pull: "Vertical Pull",
        horizontal_row: "Horizontal Row",
        biceps_curl: "Elbow Flexion",
        triceps_extension: "Elbow Extension",
        delt_fly: "Shoulder Abduction",
        calves: "Ankle Plantarflexion",
        core: "Trunk Stability & Flexion",
        full_body: "Compound Athletic Movement"
    },
    uk: {
        horizontal_press: "Горизонтальний жим",
        incline_press: "Похилий жим",
        decline_press: "Жим головою вниз",
        overhead_press: "Вертикальний жим",
        squat: "Патерн присідання",
        hinge: "Тазово-домінантний рух (тяга)",
        lunge: "Одноопорний патерн ніг (випади)",
        vertical_pull: "Вертикальна тяга",
        horizontal_row: "Горизонтальна тяга",
        biceps_curl: "Згинання передпліччя",
        triceps_extension: "Розгинання передпліччя",
        delt_fly: "Розведення плечей",
        calves: "Підйом на носки",
        core: "Стабілізація та згинання кору",
        full_body: "Багатосуглобовий комплексний рух"
    }
};

export function inferMovementPattern(catalogExercise) {
    const id = (catalogExercise.id || '').toLowerCase();
    const primaryMuscle = (catalogExercise.primaryMuscle || '').toLowerCase();

    if (id.includes('incline') && (id.includes('press') || id.includes('bench'))) return 'incline_press';
    if (id.includes('decline') && (id.includes('press') || id.includes('bench'))) return 'decline_press';
    if (id.includes('bench') || id.includes('push_up') || id.includes('floor_press') || (primaryMuscle === 'chest' && id.includes('press'))) return 'horizontal_press';
    if (id.includes('overhead') || id.includes('shoulder_press') || id.includes('military') || id.includes('push_press') || id.includes('landmine_press')) return 'overhead_press';
    if (id.includes('squat') || id.includes('leg_press') || id.includes('hack_squat')) return 'squat';
    if (id.includes('lunge') || id.includes('split_squat') || id.includes('step_up') || id.includes('bulgarian')) return 'lunge';
    if (id.includes('deadlift') || id.includes('romanian') || id.includes('good_morning') || id.includes('hip_thrust') || id.includes('glute_bridge')) return 'hinge';
    if (id.includes('pull_up') || id.includes('chin_up') || id.includes('pulldown') || id.includes('lat_pull')) return 'vertical_pull';
    if (id.includes('row') || id.includes('face_pull')) return 'horizontal_row';
    if (id.includes('curl') || primaryMuscle === 'biceps') return 'biceps_curl';
    if (id.includes('tricep') || id.includes('pushdown') || id.includes('skull_crusher') || id.includes('dips')) return 'triceps_extension';
    if (id.includes('raise') || id.includes('fly') || primaryMuscle === 'rear_deltoids') return 'delt_fly';
    if (id.includes('calf') || primaryMuscle === 'calves') return 'calves';
    if (id.includes('plank') || id.includes('crunch') || id.includes('twist') || id.includes('leg_raise') || id.includes('rollout') || primaryMuscle === 'abs' || primaryMuscle === 'obliques') return 'core';

    if (primaryMuscle === 'chest') return 'horizontal_press';
    if (primaryMuscle === 'upper_back' || primaryMuscle === 'back') return 'horizontal_row';
    if (primaryMuscle === 'quadriceps' || primaryMuscle === 'legs') return 'squat';
    if (primaryMuscle === 'hamstrings' || primaryMuscle === 'glutes' || primaryMuscle === 'lower_back') return 'hinge';
    if (primaryMuscle === 'deltoids') return 'overhead_press';

    return 'full_body';
}

export function generateMovementPhases(pattern, langKey) {
    const isUk = langKey === 'uk';
    switch (pattern) {
        case 'horizontal_press':
        case 'incline_press':
        case 'decline_press':
            return [
                {
                    name: isUk ? "Вихідне положення та фіксація" : "Setup & Lockout",
                    label: isUk ? "СТАРТ" : "START",
                    cue: isUk ? "Лопатки зведені й притиснуті до лави, руки на ширині плечей над грудьми." : "Shoulder blades retracted and pinned down, arms locked out perpendicular to chest."
                },
                {
                    name: isUk ? "Точка реверсу" : "Chest Touch / Inflection",
                    label: isUk ? "РЕВЕРС" : "INFLECTION",
                    cue: isUk ? "Опускайте вагу контрольовано з ліктями під кутом 45°–70°. Дотик без відбивання." : "Lower with elbows tucked 45°–70° to torso. Touch lower sternum with zero bounce."
                },
                {
                    name: isUk ? "Траєкторія витискання" : "Concentric Drive Arc",
                    label: isUk ? "ФІНІШ" : "FINISH",
                    cue: isUk ? "Вичавлюйте штангу чи гантелі вгору за природною дугою, не втрачаючи зведення лопаток." : "Press upward and slightly back toward eye-line in an athletic arc without losing shoulder retraction."
                }
            ];
        case 'squat':
            return [
                {
                    name: isUk ? "Вихідне положення та натяг кору" : "Setup & 360° Brace",
                    label: isUk ? "СТАРТ" : "START",
                    cue: isUk ? "Стійка на ширині плечей, грудна клітка піднята, глибокий вдих у живіт із напруженням кору." : "Feet shoulder-width apart, chest proud, deep diaphragmatic breath into braced core."
                },
                {
                    name: isUk ? "Глибина паралелі" : "Parallel Depth",
                    label: isUk ? "РЕВЕРС" : "INFLECTION",
                    cue: isUk ? "Опускайтеся до паралелі стегон із підлогою або нижче. Коліна розведені за лінією носків." : "Descend until hip crease breaks parallel with knees. Knees track over toes with balanced midfoot."
                },
                {
                    name: isUk ? "Підйом та фіксація" : "Drive & Stand",
                    label: isUk ? "ФІНІШ" : "FINISH",
                    cue: isUk ? "Тисніть у підлогу серединою стопи, піднімаючи таз і груди синхронно до повної фіксації сідницями." : "Drive through midfoot and heels, standing tall with simultaneous hip and knee extension."
                }
            ];
        case 'hinge':
            return [
                {
                    name: isUk ? "Вихідне положення та натяг" : "Setup & Tension Wedge",
                    label: isUk ? "СТАРТ" : "START",
                    cue: isUk ? "Гриф над серединою стопи, спина пряма, найширші м'язи напружені, люфт вибрано." : "Bar over midfoot, neutral spine, lats packed tight, slack pulled out of bar."
                },
                {
                    name: isUk ? "Проходження колін" : "Knee Clearance Drive",
                    label: isUk ? "РЕВЕРС" : "INFLECTION",
                    cue: isUk ? "Поштовх ногами у підлогу. Гриф ковзає впритул по гомілках зі збереженням кута нахилу спини." : "Leg-press floor away. Bar stays glued against shins as knees and hips open in unison."
                },
                {
                    name: isUk ? "Фіксація у верхній точці" : "Lockout & Glute Squeeze",
                    label: isUk ? "ФІНІШ" : "FINISH",
                    cue: isUk ? "Виштовхніть таз уперед за рахунок сідниць. Стійте строго вертикально без прогину попереку назад." : "Snap hips forward to complete glute contraction. Stand tall with neutral spine without leaning backward."
                }
            ];
        case 'vertical_pull':
        case 'horizontal_row':
            return [
                {
                    name: isUk ? "Вихідне положення та розтягнення" : "Dead Hang / Full Stretch",
                    label: isUk ? "СТАРТ" : "START",
                    cue: isUk ? "Руки витягнуті, лопатки опущені донизу (активний вис або фіксація корпусу)." : "Full reach with active scapular depression before initiating pull."
                },
                {
                    name: isUk ? "Пікове скорочення" : "Peak Contraction",
                    label: isUk ? "РЕВЕРС" : "INFLECTION",
                    cue: isUk ? "Тягніть лікті назад до талії, зведіть лопатки та затримайтеся на 1 секунду." : "Drive elbows back toward hips, retract scapulae fully, and hold peak contraction for 1s."
                },
                {
                    name: isUk ? "Контрольоване повернення" : "Eccentric Return",
                    label: isUk ? "ФІНІШ" : "FINISH",
                    cue: isUk ? "Повільно повертайтеся у вихідне положення протягом 2–3 секунд під повним контролем." : "Return under control over 2–3 seconds while maintaining continuous lat tension."
                }
            ];
        default:
            return [
                {
                    name: isUk ? "Вихідне положення" : "Setup & Alignment",
                    label: isUk ? "СТАРТ" : "START",
                    cue: isUk ? "Стабільна атлетична стійка, кор напружений, погляд зафіксований." : "Stable athletic posture, core engaged, body aligned with gravity line."
                },
                {
                    name: isUk ? "Пікове навантаження" : "Peak Effort / Stretch",
                    label: isUk ? "РЕВЕРС" : "INFLECTION",
                    cue: isUk ? "Контрольований рух у повній амплітуді без ривків чи інерції." : "Controlled execution through full active range of motion without momentum."
                },
                {
                    name: isUk ? "Повернення у старт" : "Controlled Reset",
                    label: isUk ? "ФІНІШ" : "FINISH",
                    cue: isUk ? "Плавне повернення у вихідне положення зі збереженням напруження цільового м'яза." : "Smooth return to start position, preserving continuous muscle tension throughout."
                }
            ];
    }
}

export function generatePostureChecks(pattern, langKey) {
    const isUk = langKey === 'uk';
    switch (pattern) {
        case 'horizontal_press':
        case 'incline_press':
        case 'decline_press':
            return {
                head: isUk
                    ? "Потилиця щільно притиснута до лави, погляд строго під грифом на старті. Заборонено відривати або крутити головою під час жиму."
                    : "Back of head firmly cemented to bench pad; eyes directly aligned beneath racked bar. Never crane neck or lift head during press.",
                legs: isUk
                    ? "Стопи всією площею втиснуті в підлогу. Тисніть п'ятами назад («leg drive») для створення монолітного натягу корпусу без відриву таза."
                    : "Feet planted flat on floor; push through heels ('leg drive') to transfer ground force into torso tightness without lifting hips."
            };
        case 'squat':
        case 'lunge':
            return {
                head: isUk
                    ? "Голова на одній лінії з хребтом, погляд спрямований у точку на підлозі на 2–3 метри перед собою. Не закидайте шию назад."
                    : "Head packed in line with neutral spine; gaze fixed on floor 6–10 feet ahead. Avoid hyperextending neck upward.",
                legs: isUk
                    ? "Постановка стоп на ширині плечей, носки розгорнуті на 15°–30°. Розривайте підлогу в сторони, коліна строго за напрямком носків."
                    : "Feet shoulder-width, toes angled 15°–30° outward; spread the floor and ensure knees track directly over toes."
            };
        case 'hinge':
            return {
                head: isUk
                    ? "Шийний відділ нейтральний, підборіддя злегка підібране, погляд на підлогу перед собою. Не задирайте голову догори під час зриву."
                    : "Cervical spine neutral; chin slightly tucked, gaze directed at floor ahead. Do not crank neck up toward mirror.",
                legs: isUk
                    ? "Гриф строго над серединою стопи, ширина таза. Поштовх виконується всією площею стопи та п'ятами для ввімкнення сідниць і задньої поверхні."
                    : "Bar directly over midfoot with hip-width stance. Push the floor away through midfoot and heels to engage hamstrings and glutes."
            };
        case 'overhead_press':
            return {
                head: isUk
                    ? "Злегка відведіть підборіддя назад під час проходження грифа біля обличчя, потім поверніть голову вперед у «вікно» під штангою."
                    : "Pull chin back slightly as bar passes face, then push head forward into the 'window' beneath bar at lockout.",
                legs: isUk
                    ? "Ноги на ширині плечей, коліна заблоковані, сідниці міцно стиснуті для запобігання прогину в попереку."
                    : "Feet shoulder-width apart, knees locked, glutes squeezed tightly to prevent lower back hyperextension."
            };
        case 'vertical_pull':
        case 'horizontal_row':
            return {
                head: isUk
                    ? "Голова на одній лінії з хребтом, не тягніться підборіддям уперед. Дозвольте грудям рухатися назустріч рукам."
                    : "Head stays in neutral alignment with torso; avoid craning chin forward to meet the bar.",
                legs: isUk
                    ? "Коліна злегка зігнуті, стопи надійно зафіксовані в упорі або на підлозі для створення стабільної основи."
                    : "Knees slightly soft, feet braced securely on floor or pads to provide an unshakeable pulling foundation."
            };
        default:
            return {
                head: isUk
                    ? "Зберігайте нейтральне положення голови та шийного відділу без надмірного нахилу чи перерозгинання."
                    : "Maintain neutral cervical spine alignment without excessive flexion or hyperextension.",
                legs: isUk
                    ? "Стопи міцно притиснуті до підлоги на ширині плечей для забезпечення стійкої рівноваги."
                    : "Feet firmly planted with balanced weight distribution to maintain solid athletic balance."
            };
    }
}

/**
 * Main accessor: retrieves full exercise guide with instructions, mistakes, tips, volume, movement phases, and posture checks.
 */
export function getExerciseGuide(exerciseId, lang = 'en') {
    const isUk = (lang || 'en').startsWith('uk');
    const langKey = isUk ? 'uk' : 'en';

    const baseExercise = findExercise(exerciseId, isUk);
    const catalogExercise = baseExercise || { id: exerciseId, name: exerciseId };

    const curated = CURATED_GUIDES[exerciseId] && CURATED_GUIDES[exerciseId][langKey];

    const localizedTitle = (isUk ? catalogExercise.nameUk || catalogExercise.name : catalogExercise.name)
        || (exerciseId ? exerciseId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "");

    const primaryMuscle = catalogExercise.primaryMuscle || 'chest';
    const secondaryMuscles = (catalogExercise.muscleGroups || [])
        .filter(mg => mg.priority !== 'primary')
        .map(mg => mg.group);

    const equipName = (EQUIPMENT_NAMES[langKey] && EQUIPMENT_NAMES[langKey][catalogExercise.equipment])
        || catalogExercise.equipment
        || (isUk ? "Спортивне спорядження" : "Equipment");

    // Dynamic guide fallback
    const dynamic = generateDynamicGuide(catalogExercise, langKey);

    const instructions = (catalogExercise.instructions && catalogExercise.instructions.length > 0)
        ? catalogExercise.instructions
        : (curated?.instructions || dynamic.instructions);

    const commonMistakes = (catalogExercise.commonMistakes && catalogExercise.commonMistakes.length > 0)
        ? catalogExercise.commonMistakes
        : (curated?.commonMistakes || dynamic.commonMistakes);

    const tips = curated?.tips || dynamic.tips;
    const volume = curated?.volume || dynamic.volume;

    const pattern = inferMovementPattern(catalogExercise);
    const patternName = (PATTERN_NAMES[langKey] && PATTERN_NAMES[langKey][pattern]) || pattern;

    // Movement phases and posture checks (head & legs)
    const phases = (curated?.phases && curated.phases.length > 0)
        ? curated.phases
        : generateMovementPhases(pattern, langKey);

    const postureChecks = curated?.postureChecks || generatePostureChecks(pattern, langKey);

    const diagramUrl = catalogExercise.diagramUrl || null;
    const cardAsset = `/images/exercises/${exerciseId}.webp`;
    const patternAsset = `/images/exercises/patterns/${pattern}.webp`;
    const muscleAssetName = MUSCLE_IMAGE_MAP[primaryMuscle] || 'Chest';
    const muscleAsset = `/images/muscles/${muscleAssetName}.webp`;

    return {
        id: exerciseId,
        name: localizedTitle,
        equipment: equipName,
        rawEquipment: catalogExercise.equipment,
        primaryMuscle,
        primaryMuscleName: (MUSCLE_NAMES[langKey] && MUSCLE_NAMES[langKey][primaryMuscle]) || primaryMuscle,
        secondaryMuscles,
        instructions,
        commonMistakes,
        tips,
        volume,
        diagramUrl,
        pattern,
        patternName,
        phases,
        postureChecks,
        cardAsset,
        patternAsset,
        muscleAsset
    };
}

